/**
 * Feed / token health-watchdog cores — Phase 6a-3 commit 8 of the
 * decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A25).
 * The DECISION skeletons shared by index.html and paper.html; every effect,
 * probe, and HTTP mechanism stays page-side (the two terminals deliberately
 * differ — R9: never merge the two behaviours).
 *
 * MULTI-INSTANCE SERVICE RULE: this module holds ZERO module-level mutable
 * state — every instance's state lives in its own closure, so the index and
 * paper instances can run simultaneously without sharing anything. (Contrast
 * panels/backtest, a genuine page singleton where module state is fine.)
 *
 * TRANSITION MEMORY IS EXTERNALLY OWNED (isMarked): on index.html the
 * stale-memory is the DOM class itself, which updateStatus() resets
 * independently on WS open/close — a core-owned boolean would desync after a
 * reconnect and never re-mark the dot. The core therefore READS memory
 * through the probe every cycle and never stores it.
 */

import { isMarketHoursNow } from "./market_time";

export interface FeedHealthContext {
  /** Is the page's feed transport up? (index: state.ws OPEN; paper: wsMap non-empty) */
  isActive(): boolean;
  /** Externally-owned stale-transition memory (index: dot classList; paper: state.feedStale). */
  isMarked(): boolean;
  /** Last tick wall-clock ms, falsy = never ticked (both pages: state.lastTickTime). */
  getLastTickMs(): number | null;
  /** stale while unmarked — mark + warn (+ page-specific toast/render). */
  onBecameStale(secs: number): void;
  /** stale while already marked (index: refresh the seconds text; paper: no-op). */
  onStillStale(secs: number): void;
  /** fresh while marked — clear (index: class+text; paper: flags+render). */
  onRecovered(): void;
  /** transport down or outside market hours while marked — clear. */
  onInactiveClear(): void;
  staleThresholdMs?: number;
  intervalMs?: number;
}

/** Branch skeleton moved verbatim from both pages' checkFeedHealth. No
 * immediate first run (both pages installed a bare setInterval). */
export function startFeedHealthWatch(ctx: FeedHealthContext): void {
  const threshold = ctx.staleThresholdMs ?? 30000;
  const check = () => {
    if (!ctx.isActive() || !isMarketHoursNow()) {
      if (ctx.isMarked()) ctx.onInactiveClear();
      return;
    }
    const last = ctx.getLastTickMs();
    const sinceLastTick = last ? Date.now() - last : null;
    const stale = sinceLastTick != null && sinceLastTick > threshold;
    if (stale && !ctx.isMarked()) {
      ctx.onBecameStale(Math.round(sinceLastTick! / 1000));
    } else if (stale) {
      ctx.onStillStale(Math.round(sinceLastTick! / 1000));
    } else if (!stale && ctx.isMarked()) {
      ctx.onRecovered();
    }
  };
  setInterval(check, ctx.intervalMs ?? 5000);
}

export interface TokenHealthContext {
  /** Gate BEFORE any network call (both pages: banner element exists) —
   * preserves the original "no banner, no fetch" short-circuit. */
  shouldCheck(): boolean;
  /** Page-owned HTTP (index: fetch+authHeaders; paper: apiFetch). */
  fetchStatus(): Promise<{ ok: boolean; json(): Promise<any> }>;
  /** Page-owned banner mechanics (index: style.display; paper: classList). */
  onStatus(status: any): void;
  intervalMs?: number;
}

/** Immediate first check, then the interval — preserving both pages'
 * original install order (checkTokenHealth(); setInterval(...)). Network
 * errors are swallowed (a hiccup checking status isn't itself alarming). */
export function startTokenHealthWatch(ctx: TokenHealthContext): void {
  const check = async () => {
    if (!ctx.shouldCheck()) return;
    try {
      const resp = await ctx.fetchStatus();
      if (!resp.ok) return;
      const status = await resp.json();
      ctx.onStatus(status);
    } catch (e) {}
  };
  check();
  setInterval(check, ctx.intervalMs ?? 60000);
}
